import { getExtension } from '@medplum/core';
import { Communication, Extension, Practitioner, Reference } from '@medplum/fhirtypes';
import { CallRoutingSkipReason } from './constants';
import {
  INCOMING_CALL_ROUTING_HISTORY_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_PROVIDER_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_REASON_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_RUNG_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_TARGET_EXTENSION,
  INCOMING_CALL_ROUTING_SKIP_TIMESTAMP_EXTENSION,
} from './extensions';

/**
 * One routing candidate that was passed over, and why.
 *
 * `provider` is absent when the whole rung had nobody to ring — "the PN pool was empty" is
 * a routing decision someone has to be able to see, and it has no practitioner to hang off.
 */
export interface CallRoutingSkip {
  reason: CallRoutingSkipReason;
  provider?: Reference<Practitioner>;
  /** Zero-based position in the configured chain. */
  rung?: number;
  /** Human-readable rung description, e.g. "patient-navigator pool". */
  target?: string;
  timestamp?: string;
}

const CALL_ROUTING_SKIP_REASONS = new Set<string>(Object.values(CallRoutingSkipReason));

function isCallRoutingSkipReason(value: string | undefined): value is CallRoutingSkipReason {
  return !!value && CALL_ROUTING_SKIP_REASONS.has(value);
}

/** Serialises one skipped attempt to a nested reason/provider/rung/target group. */
export function buildCallRoutingSkipExtension(skip: CallRoutingSkip): Extension {
  const nested: Extension[] = [{ url: INCOMING_CALL_ROUTING_SKIP_REASON_EXTENSION, valueCode: skip.reason }];

  if (skip.provider) {
    nested.push({ url: INCOMING_CALL_ROUTING_SKIP_PROVIDER_EXTENSION, valueReference: skip.provider });
  }

  if (skip.rung !== undefined) {
    nested.push({ url: INCOMING_CALL_ROUTING_SKIP_RUNG_EXTENSION, valueInteger: skip.rung });
  }

  if (skip.target) {
    nested.push({ url: INCOMING_CALL_ROUTING_SKIP_TARGET_EXTENSION, valueString: skip.target });
  }

  if (skip.timestamp) {
    nested.push({ url: INCOMING_CALL_ROUTING_SKIP_TIMESTAMP_EXTENSION, valueDateTime: skip.timestamp });
  }

  return { url: INCOMING_CALL_ROUTING_SKIP_EXTENSION, extension: nested };
}

function parseCallRoutingSkipExtension(extension: Extension): CallRoutingSkip | undefined {
  const nested = extension.extension ?? [];
  const reason = nested.find((ext) => ext.url === INCOMING_CALL_ROUTING_SKIP_REASON_EXTENSION)?.valueCode;

  if (!isCallRoutingSkipReason(reason)) {
    return undefined;
  }

  return {
    reason,
    provider: nested.find((ext) => ext.url === INCOMING_CALL_ROUTING_SKIP_PROVIDER_EXTENSION)?.valueReference as
      | Reference<Practitioner>
      | undefined,
    rung: nested.find((ext) => ext.url === INCOMING_CALL_ROUTING_SKIP_RUNG_EXTENSION)?.valueInteger,
    target: nested.find((ext) => ext.url === INCOMING_CALL_ROUTING_SKIP_TARGET_EXTENSION)?.valueString,
    timestamp: nested.find((ext) => ext.url === INCOMING_CALL_ROUTING_SKIP_TIMESTAMP_EXTENSION)?.valueDateTime,
  };
}

/** Every skipped routing attempt recorded on a call, oldest first. */
export function getCallRoutingSkips(communication: Communication): CallRoutingSkip[] {
  const entries = getExtension(communication, INCOMING_CALL_ROUTING_HISTORY_EXTENSION)?.extension ?? [];

  return entries
    .filter((entry) => entry.url === INCOMING_CALL_ROUTING_SKIP_EXTENSION)
    .map(parseCallRoutingSkipExtension)
    .filter((skip): skip is CallRoutingSkip => !!skip);
}

/**
 * Whether a routing-history child names a provider the call was offered to.
 *
 * The container now holds two kinds of child. Readers counting or dereferencing providers
 * must go through this rather than assuming every child carries a `valueReference`.
 *
 * Defined as "carries a reference and is not a skip" rather than matching the
 * `PRACTITIONER_FHIR_URL` these are written under, because the entries are years of call
 * history: a stricter rule would quietly drop any historical entry written under a
 * different url, and there is no migration to find out. Skips are the only thing being
 * excluded, so they are the only thing named.
 *
 * Structurally typed rather than taking an `Extension`, because provider-app reads the same
 * container through its GraphQL `ExtensionFragment`.
 */
export function isProviderRoutingHistoryEntry(entry: { url?: string; valueReference?: unknown }): boolean {
  return !!entry.valueReference && entry.url !== INCOMING_CALL_ROUTING_SKIP_EXTENSION;
}

/**
 * Adds skip entries to the routing-history container, returning the resource's full
 * extension array.
 *
 * Mirrors `moveRecipientToRoutingHistory`: the container is created on first use, and the
 * array is rebuilt rather than mutated so the caller can hand it straight to an update.
 */
export function appendCallRoutingSkips(communication: Communication, skips: CallRoutingSkip[]): Extension[] {
  const extensions = communication.extension ? [...communication.extension] : [];

  if (skips.length === 0) {
    return extensions;
  }

  const existingIndex = extensions.findIndex((ext) => ext.url === INCOMING_CALL_ROUTING_HISTORY_EXTENSION);
  const existing = existingIndex >= 0 ? extensions[existingIndex] : undefined;

  const updated: Extension = {
    url: INCOMING_CALL_ROUTING_HISTORY_EXTENSION,
    extension: [...(existing?.extension ?? []), ...skips.map(buildCallRoutingSkipExtension)],
  };

  if (existingIndex >= 0) {
    extensions[existingIndex] = updated;
  } else {
    extensions.push(updated);
  }

  return extensions;
}
